// 🤖 API Craft Back Office - AI Chat Page
import React, { useState, useRef, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAiChat } from '../hooks/useAiChat';
import Layout from '../components/Layout';
import ChatMessage from '../components/ChatMessage';

const AiChatPage: React.FC = () => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const { 
    messages, 
    isLoading, 
    error, 
    sendMessage, 
    clearChat, 
    exportChat, 
    hasMessages 
  } = useAiChat();

  // Set page title
  usePageTitle('AI Assistant');

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [inputMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const message = inputMessage;
    setInputMessage('');
    await sendMessage(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const suggestionQuestions = [
    "How many records do I have in total?",
    "What are my most used models?",
    "Show me a summary of my data",
    "Which vehicles does each distributor have?"
  ];

  return (
    <Layout>
      <div className="h-full flex flex-col max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-sage-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-forest-800">🤖 AI Assistant</h1>
              <p className="text-sage-600 mt-1">
                Ask anything about your data in natural language
              </p>
            </div>
            
            {hasMessages && (
              <div className="flex space-x-2">
                <button
                  onClick={exportChat}
                  className="px-3 py-2 text-sm bg-sage-100 text-sage-700 rounded-lg hover:bg-sage-200 transition-colors"
                >
                  📥 Export
                </button>
                <button
                  onClick={clearChat}
                  className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                >
                  🗑️ Clear
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          
          {/* Welcome Message */}
          {!hasMessages && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-r from-mint-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">🤖</span>
              </div>
              <h2 className="text-xl font-semibold text-forest-800 mb-3">
                Hello! I'm your data assistant
              </h2>
              <p className="text-sage-600 mb-6 max-w-md mx-auto">
                I can help you analyze your data, generate reports, and answer questions about your information.
              </p>
              
              {/* Suggestion Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                {suggestionQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setInputMessage(question)}
                    className="p-3 text-left bg-mint-50 border border-mint-200 rounded-lg hover:bg-mint-100 transition-colors text-sm"
                  >
                    💬 {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chat Messages */}
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="flex justify-start mb-4">
              <div className="bg-white border border-sage-200 rounded-r-2xl rounded-tl-2xl shadow-sm p-4">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-mint-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-mint-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-mint-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm text-sage-600">Analyzing data...</span>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 bg-white border-t border-sage-200 p-6">
          <form onSubmit={handleSubmit} className="flex space-x-4">
            <div className="flex-1">
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question here... (Enter to send, Shift+Enter for new line)"
                className="w-full px-4 py-3 border border-sage-300 rounded-xl resize-none overflow-hidden min-h-[50px] max-h-32 focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                disabled={isLoading}
                rows={1}
              />
            </div>
            <button
              type="submit"
              disabled={!inputMessage.trim() || isLoading}
              className="px-6 py-3 bg-mint-500 text-white rounded-xl hover:bg-mint-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isLoading ? '⏳' : '📤'}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AiChatPage;
